import React from 'react';

// Button component for actions
const ButtonComponent = ({ 
  text = 'Button',
  url = '#',
  variant = 'primary',
  size = 'md',
  target = '_self',
  icon = 'none',
  align = 'left'
}) => {
  const getVariantClass = () => {
    switch (variant) {
      case 'primary': return 'bg-blue-600 hover:bg-blue-700 text-white border-transparent';
      case 'secondary': return 'bg-slate-200 hover:bg-slate-300 text-slate-900 border-transparent dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-100';
      case 'outline': return 'bg-transparent hover:bg-blue-50 text-blue-600 border-blue-600 dark:hover:bg-blue-900/20 dark:text-blue-400 dark:border-blue-400';
      case 'ghost': return 'bg-transparent hover:bg-slate-100 text-slate-700 border-transparent dark:hover:bg-slate-800 dark:text-slate-300';
      case 'success': return 'bg-green-600 hover:bg-green-700 text-white border-transparent';
      case 'warning': return 'bg-yellow-600 hover:bg-yellow-700 text-white border-transparent';
      case 'danger': return 'bg-red-600 hover:bg-red-700 text-white border-transparent';
      default: return 'bg-blue-600 hover:bg-blue-700 text-white border-transparent';
    }
  };

  const getSizeClass = () => {
    switch (size) {
      case 'xs': return 'px-2.5 py-1.5 text-xs';
      case 'sm': return 'px-3 py-2 text-sm';
      case 'md': return 'px-4 py-2 text-sm';
      case 'lg': return 'px-4 py-2 text-base';
      case 'xl': return 'px-6 py-3 text-base';
      default: return 'px-4 py-2 text-sm';
    }
  };

  const getAlignClass = () => {
    switch (align) {
      case 'left': return 'justify-start';
      case 'center': return 'justify-center';
      case 'right': return 'justify-end';
      default: return 'justify-start';
    }
  };

  const getIcon = () => {
    switch (icon) {
      case 'arrow-right': return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      );
      case 'arrow-left': return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      );
      case 'external': return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      );
      case 'download': return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
      case 'mail': return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      );
      case 'phone': return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      );
      default: return null;
    }
  };

  const buttonClasses = `
    inline-flex items-center gap-2 font-medium rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
    ${getVariantClass()}
    ${getSizeClass()}
  `;

  const containerClasses = `flex ${getAlignClass()}`;

  // Helper function to format URL properly
  const formatUrl = (url) => {
    if (!url || url === '#') return '#';
    
    // If URL already has protocol, return as is
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('mailto:') || url.startsWith('tel:')) {
      return url;
    }
    
    // If URL starts with //, it's protocol-relative, return as is
    if (url.startsWith('//')) {
      return url;
    }
    
    // If URL starts with /, it's an absolute path on the same domain
    if (url.startsWith('/')) {
      return url;
    }
    
    // For domain names without protocol, add https://
    if (url.includes('.') && !url.includes(' ')) {
      return `https://${url}`;
    }
    
    // For everything else, return as is (could be relative paths, anchors, etc.)
    return url;
  };

  return (
    <div className={containerClasses}>
      <a
        href={formatUrl(url)}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        className={buttonClasses}
      >
        {icon !== 'none' && icon !== 'arrow-right' && icon !== 'external' && getIcon()}
        <span>{text}</span>
        {(icon === 'arrow-right' || icon === 'external') && getIcon()}
      </a>
    </div>
  );
};

export const Button = {
  fields: {
    text: {
      type: 'text',
      label: 'Button Text'
    },
    url: {
      type: 'text',
      label: 'Link (URL)',
      placeholder: 'https://example.com oder example.com'
    },
    variant: {
      type: 'select',
      label: 'Button-Stil',
      options: [
        { label: 'Primär (Blau)', value: 'primary' },
        { label: 'Sekundär (Grau)', value: 'secondary' },
        { label: 'Umriss', value: 'outline' },
        { label: 'Transparent', value: 'ghost' },
        { label: 'Erfolg (Grün)', value: 'success' },
        { label: 'Warnung (Gelb)', value: 'warning' },
        { label: 'Gefahr (Rot)', value: 'danger' }
      ]
    },
    size: {
      type: 'select',
      label: 'Größe',
      options: [
        { label: 'Sehr klein (XS)', value: 'xs' },
        { label: 'Klein (SM)', value: 'sm' },
        { label: 'Mittel (MD)', value: 'md' },
        { label: 'Groß (LG)', value: 'lg' },
        { label: 'Sehr groß (XL)', value: 'xl' }
      ]
    },
    target: {
      type: 'select',
      label: 'Link-Ziel',
      options: [
        { label: 'Gleicher Tab', value: '_self' },
        { label: 'Neuer Tab', value: '_blank' }
      ]
    },
    icon: {
      type: 'select',
      label: 'Icon',
      options: [
        { label: 'Kein Icon', value: 'none' },
        { label: 'Pfeil rechts', value: 'arrow-right' },
        { label: 'Pfeil links', value: 'arrow-left' },
        { label: 'Externer Link', value: 'external' },
        { label: 'Download', value: 'download' },
        { label: 'E-Mail', value: 'mail' },
        { label: 'Telefon', value: 'phone' }
      ]
    },
    align: {
      type: 'select',
      label: 'Ausrichtung',
      options: [
        { label: 'Links', value: 'left' },
        { label: 'Zentriert', value: 'center' },
        { label: 'Rechts', value: 'right' }
      ]
    }
  },
  render: ButtonComponent
};
